import React, { createContext, useState, useEffect, useMemo } from "react";
import useAuth from "../hooks/useAuth";
import useDialog from "../hooks/useDialog";
import {
  fetchCurrentSubscription,
  createCheckoutSession,
  createBillingPortalSession,
} from "../api/subscription";
import { toast } from "react-toastify";

export const SubscriptionContext = createContext(null);

// Cache duration 2 minutes
const REFRESH_INTERVAL = 2 * 60 * 1000;

// LocalStorage key for caching
const SUBSCRIPTION_CACHE_KEY = "subscription_cache";
const TRIAL_EXPIRED_SHOWN_KEY = "trial_expired_dialog_shown";

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const { openTrialExpiredDialog } = useDialog();
  const [subscription, setSubscription] = useState(null);
  const [isFetchingSubscription, setIsFetchingSubscription] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [previousTrialStatus, setPreviousTrialStatus] = useState(null);

  // Clear localStorage subscription data
  const clearSubscriptionCache = () => {
    try {
      localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
    } catch (error) {
      console.error("Error clearing subscription cache:", error);
    }
  };

  // Check if trial expired dialog has been shown for this user
  const hasTrialExpiredDialogBeenShown = () => {
    try {
      const shownUsers = JSON.parse(
        localStorage.getItem(TRIAL_EXPIRED_SHOWN_KEY) || "[]"
      );
      return user ? shownUsers.includes(user.uid) : false;
    } catch (error) {
      console.error("Error checking trial expired dialog status:", error);
      return false;
    }
  };

  // Mark trial expired dialog as shown for this user
  const markTrialExpiredDialogAsShown = () => {
    try {
      const shownUsers = JSON.parse(
        localStorage.getItem(TRIAL_EXPIRED_SHOWN_KEY) || "[]"
      );
      if (user && !shownUsers.includes(user.uid)) {
        shownUsers.push(user.uid);
        localStorage.setItem(
          TRIAL_EXPIRED_SHOWN_KEY,
          JSON.stringify(shownUsers)
        );
      }
    } catch (error) {
      console.error("Error marking trial expired dialog as shown:", error);
    }
  };

  // Load cached subscription data from localStorage
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
      if (cachedData) {
        const {
          subscription: cachedSubscription,
          userId,
          timestamp,
        } = JSON.parse(cachedData);

        const isValidCache =
          userId === user?.uid && Date.now() - timestamp < 24 * 60 * 60 * 1000;

        if (isValidCache && cachedSubscription?.status === "active") {
          setSubscription(cachedSubscription);
        } else {
          clearSubscriptionCache();
        }
      }
    } catch (error) {
      console.error("Error loading cached subscription data:", error);
    }

    setIsInitialized(true);
  }, [user?.uid]);

  // Save subscription data to localStorage
  useEffect(() => {
    if (subscription && user) {
      if (subscription.status === "active") {
        try {
          const cacheData = {
            subscription,
            userId: user.uid,
            timestamp: Date.now(),
          };
          localStorage.setItem(
            SUBSCRIPTION_CACHE_KEY,
            JSON.stringify(cacheData)
          );
        } catch (error) {
          console.error("Error caching subscription data:", error);
        }
      } else {
        clearSubscriptionCache();
      }
    } else {
      clearSubscriptionCache();
    }
  }, [subscription, user]);

  // Fetch subscription data
  const fetchSubscription = async (force = false) => {
    // Don't fetch if loading
    if (isFetchingSubscription) return;

    // Don't fetch if we recently fetched
    if (
      !force &&
      lastFetchTime &&
      Date.now() - lastFetchTime < REFRESH_INTERVAL
    ) {
      return;
    }

    // Only fetch if user is logged in
    if (!user) {
      setSubscription(null);
      clearSubscriptionCache();
      return;
    }

    try {
      setIsFetchingSubscription(true);
      setError(null);

      const response = await fetchCurrentSubscription();

      // Check if the subscription status has changed
      const statusChanged =
        subscription &&
        response.subscription &&
        subscription.status !== response.subscription.status;

      setSubscription(response.subscription);
      setLastFetchTime(Date.now());

      // If status changed update the UI
      if (statusChanged) {
        console.log(
          "Subscription status changed:",
          response.subscription.status
        );
        if (response.subscription.status !== "active") {
          clearSubscriptionCache();
        }
      }
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError("Failed to load subscription data");
      if (err.status === 404 || err.message?.includes("not found")) {
        setSubscription(null);
        clearSubscriptionCache();
      }
    } finally {
      setIsFetchingSubscription(false);
    }
  };

  // Fetch subscription when user logs in
  useEffect(() => {
    if (user && isInitialized) {
      fetchSubscription(true);
    } else if (!user) {
      setSubscription(null);
      setLastFetchTime(null);
      clearSubscriptionCache();
    }
  }, [user, isInitialized]);

  // Set up periodic refresh and activity-based refresh
  useEffect(() => {
    if (!user) return;

    // Refresh subscription data periodically
    const refreshInterval = setInterval(() => {
      fetchSubscription();
    }, REFRESH_INTERVAL);

    // Also refresh after user activity (window focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchSubscription();
      }
    };

    // Also refresh after a user returns to the app
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", () => fetchSubscription());

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", () => fetchSubscription());
    };
  }, [user]);

  // Redirect to Stripe checkout to subscribe
  const subscribe = async (
    planId,
    successUrl,
    cancelUrl,
    isQuarterly = false
  ) => {
    try {
      setIsCreatingSubscription(true);
      setError(null);

      const data = {
        planId: planId,
        successUrl:
          successUrl ||
          `${window.location.origin}/settings?tab=subscription&status=success`,
        cancelUrl: cancelUrl || `${window.location.origin}?status=cancel`,
        isQuarterly: isQuarterly,
      };

      const response = await createCheckoutSession(data);

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      console.error("Error creating checkout session:", err);
      toast.error("Failed to initiate checkout process");
      setIsCreatingSubscription(false);
    }
  };

  // Redirect to Stripe billing portal to manage subscription
  const manageSubscription = async (returnUrl) => {
    try {
      setIsManagingSubscription(true);
      setError(null);

      const data = {
        returnUrl: returnUrl || window.location.href,
      };

      const response = await createBillingPortalSession(data);

      // Redirect to Stripe Billing Portal
      window.open(response.url, "_blank");
    } catch (err) {
      console.error("Error accessing billing portal:", err);
      toast.error("Failed to access billing portal");
    } finally {
      setIsManagingSubscription(false);
    }
  };

  // Memoize subscription status
  const subscriptionStatus = useMemo(() => {
    const hasActive = !!subscription && subscription.status === "active";
    const hasPremium =
      hasActive &&
      (subscription?.plan === "Premium Monthly" ||
        subscription?.plan === "Premium Quarterly");
    const isQuarterly =
      hasActive &&
      (subscription?.plan === "Premium Quarterly" ||
        subscription?.interval === "quarter" ||
        subscription?.billingCycle === "quarterly");

    // Check trial status
    const isInTrial =
      user && user.trialEndDate
        ? new Date() < new Date(user.trialEndDate)
        : false;

    const trialDaysRemaining =
      user && user.trialEndDate
        ? (() => {
            const endDate = new Date(user.trialEndDate);
            const now = new Date();
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return Math.max(0, diffDays);
          })()
        : 0;

    const daysRemaining =
      hasActive && subscription?.currentPeriodEnd
        ? (() => {
            const endDate = new Date(subscription.currentPeriodEnd);
            const now = new Date();
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return Math.max(0, diffDays);
          })()
        : 0;

    // Check if trial has just expired
    const hasTrialExpired =
      user && user.trialEndDate && !isInTrial && !hasActive;

    // User has premium access if they have active subscription OR are in trial
    const hasActiveAccess = hasActive || isInTrial;
    const hasPremiumAccess = hasPremium || isInTrial;

    return {
      hasActive: hasActiveAccess,
      hasPremium: hasPremiumAccess,
      isQuarterly,
      daysRemaining,
      isInTrial,
      trialDaysRemaining,
      hasTrialExpired,
    };
  }, [subscription, user]);

  useEffect(() => {
    if (!user || !isInitialized) return;

    const currentTrialStatus = subscriptionStatus.isInTrial;

    // Check if trial has just expired
    if (
      previousTrialStatus === true &&
      currentTrialStatus === false &&
      subscriptionStatus.hasTrialExpired &&
      !hasTrialExpiredDialogBeenShown()
    ) {
      // Show trial expired dialog
      openTrialExpiredDialog();
      markTrialExpiredDialogAsShown();
    }

    // Update previous trial status
    setPreviousTrialStatus(currentTrialStatus);
  }, [
    subscriptionStatus.isInTrial,
    subscriptionStatus.hasTrialExpired,
    user,
    isInitialized,
    previousTrialStatus,
  ]);

  const value = {
    subscription,
    loading: {
      isFetchingSubscription,
      isCreatingSubscription,
      isManagingSubscription,
      isInitialized,
    },
    error,
    fetchSubscription,
    subscribe,
    manageSubscription,
    subscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
